import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { CartsService } from '@/services/firebase.service';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { currentUser } = useAuth();

  // Load cart function
  const loadCart = useCallback(async () => {
    if (currentUser) {
      // Load from Firebase for logged-in users
      try {
        const firebaseCart = await CartsService.getByUserId(currentUser.uid);
        setCart(firebaseCart || []);
      } catch (error) {
        console.error('Error loading cart:', error);
        // Fallback to localStorage
        const savedCart = localStorage.getItem('bcstore_cart');
        if (savedCart) {
          try {
            setCart(JSON.parse(savedCart));
          } catch (e) {
            setCart([]);
          }
        }
      }
    } else {
      // Load from localStorage for guests
      const savedCart = localStorage.getItem('bcstore_cart');
      if (savedCart) {
        try {
          setCart(JSON.parse(savedCart));
        } catch (e) {
          setCart([]);
        }
      }
    }
  }, [currentUser]);

  // Save cart function
  const saveCart = useCallback(async () => {
    if (cart.length === 0 && !currentUser) return;
    
    if (currentUser) {
      // Save to Firebase for logged-in users
      try {
        await CartsService.save(currentUser.uid, cart);
      } catch (error) {
        console.error('Error saving cart:', error);
      }
    }
    // Always save to localStorage as backup
    localStorage.setItem('bcstore_cart', JSON.stringify(cart));
  }, [cart, currentUser]);

  // Load cart on mount and when user changes
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Save cart whenever it changes
  useEffect(() => {
    if (cart.length > 0 || currentUser) {
      saveCart();
    }
  }, [cart, saveCart, currentUser]);

  const addToCart = (product, variant, quantity = 1) => {
    const variantId = variant?.id || 'default';
    const existingItem = cart.find(
      (item) => item.product.id === product.id && (item.variant?.id || 'default') === variantId
    );

    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product.id === product.id && (item.variant?.id || 'default') === variantId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
    } else {
      setCart([...cart, { product, variant: variant || { id: 'default', color: 'Default', image: product.images?.[0] }, quantity }]);
    }
  };

  const removeFromCart = (productId, variantId) => {
    setCart(cart.filter((item) => !(item.product.id === productId && (item.variant?.id || 'default') === variantId)));
  };

  const updateQuantity = (productId, variantId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, variantId);
    } else {
      setCart(
        cart.map((item) =>
          item.product.id === productId && (item.variant?.id || 'default') === variantId
            ? { ...item, quantity }
            : item
        )
      );
    }
  };

  const clearCart = async () => {
    setCart([]);
    if (currentUser) {
      try {
        await CartsService.clear(currentUser.uid);
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    }
    localStorage.removeItem('bcstore_cart');
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.product?.price || 0) * (item.quantity || 0), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + (item.quantity || 0), 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        setIsCartOpen,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
